import React, { FC } from 'react';
import Alert from '../Alert/Alert';
import ConfirmModal from '../ConfirmModal/ConfirmModal';
import Lightbox from '../Lightbox/Lightbox';
import Modal from '../Modal/Modal';
import Toast from '../Toast/Toast';
import { UIFeedbackHooks } from '@/Shared/Types';
import ExportUI from '../ExportUI/ExportUI';


interface UIFeedbackProps {
    uiFeedbackHooks: UIFeedbackHooks
}

/**
 * =====================================================
 * Component: UIFeedback
 * =====================================================
 *
 * Generic UI feedback layer for any domain form or page.
 * Mounted typically at the GenericDomainForm or page level.
 *
 * Responsibilities:
 * - Render global feedback components:
 *    - Toast notifications
 *    - Confirmation modals
 *    - Generic modals
 *    - Alerts
 *    - Lightbox
 * - Delegates control to hooks (`useToast`, `useAlert`, etc.)
 *   to manage state and behavior of each feedback type.
 * - Keeps UI rendering separate from business logic.
 *
 * Author: Samano CASTRE
 * Date: February 18, 2026
 */
const UIFeedback: FC<UIFeedbackProps> = ({ uiFeedbackHooks }) => {

    const {
        toastHook,
        confirmHook,
        modalHook,
        alertHook,
        lightBoxHook,
        exportUIHook
    } = uiFeedbackHooks;

    return (
        <>
            {/* Toast notifications triggered by domain actions */}
            <Toast
                data={toastHook.toastData}
                onClose={toastHook.closeToast}
            />

            {/* Confirmation modal for user actions requiring approval */}
            <ConfirmModal
                data={confirmHook.confirmData}
                onConfirm={confirmHook.confirm}
                onCancel={confirmHook.cancel}
            />

            {/* Confirmation modal for user actions requiring approval */
                exportUIHook && <ExportUI
                    data={exportUIHook.exportUIData}
                    onClose={exportUIHook.closeExport}
                />
            }

            {/* Generic modal for any contextual content */}
            <Modal
                className='w-80 minH-80'
                onClose={modalHook.closeModal}
                data={modalHook.modalData}
            />

            {/* Alert component for errors, warnings, or info messages */
                alertHook && <Alert
                    data={alertHook.alertData}
                    onClose={alertHook.closeAlert}
                />
            }

            {/* Lightbox for images, documents, or other media previews */
                lightBoxHook && <Lightbox
                    data={lightBoxHook.lightBoxData}
                    onClose={lightBoxHook.closeLightBox}
                />
            }
        </>
    );
}

export default UIFeedback;
